<template>
    <div
        data-language-switcher
        @click.self="switcherClicked"
        @keydown.space="switcherClicked"
        role="button"
        tabindex="0"
        id="language-switcher"
    >
        <font-awesome-icon icon="globe" aria-hidden="true" data-language-preview />
        <strong
            data-language-preview
            id="language-switcher-preview"
        >{{ surveyLanguage.substring(0, 2) }}</strong>
        <transition name="fade">
            <div v-if="showDialog" data-dialog-background @click.self="dialogBackgroundClicked">
                <div data-dialog>
                    <header>
                        <strong
                            id="language-switcher-header"
                        >{{ showConfirm ? confirmedLanguageVariables.header : $store.getters.textResource(`rs_language_prompt_header`) }}</strong>
                        <p
                            v-if="showConfirm"
                            id="language-switcher-subtitle"
                            role="alert"
                        >{{ confirmedLanguageVariables.subtitle }}</p>
                    </header>
                    <main v-if="showConfirm === false">
                        <div
                            data-dropdown
                            ref="dropdown"
                            @click="dropdownClicked"
                            @keydown.space.exact="dropdownClicked"
                            @keydown.prevent.down.exact="move(1, $event)"
                            @keydown.prevent.up.exact="move(-1, $event)"
                            @keydown.esc.exact="focusOut"
                            tabindex="0"
                            autofocus
                            id="language-switcher-dropdown"
                        >
                            <strong
                                id="language-switcher-current-selection"
                            >{{ selectedLanguage || surveyInfo.culture }}</strong>
                            <font-awesome-icon
                                data-icon
                                :icon="selectboxIsExpanded?`angle-up`:`angle-down`"
                                aria-hidden="true"
                            />
                            <ul
                                role="listbox"
                                :aria-expanded="selectboxIsExpanded"
                                tabindex="0"
                                aria-labelledby="language-switcher-header"
                                :aria-activedescendant="`language-picker-option-${currentLanguage.language}`"
                            >
                                <li
                                    :tabindex="0"
                                    v-for="language in languages"
                                    :key="language.culture"
                                    :id="`language-switcher-option-${language.language}`"
                                    :value="language.language"
                                    role="option"
                                    ref="select-items"
                                    @click.self="optionSelected(language, $event)"
                                    @keydown.prevent.space.exact="optionSelected(language, $event)"
                                    @keydown.esc.exact="focusOut"
                                    @keydown.enter.exact="optionSelected(language, $event)"
                                    @keydown.prevent.tab.exact="move(1, $event)"
                                    @keydown.shift.tab="move(-1, $event)"
                                >{{ language.translatedName }}</li>
                            </ul>
                        </div>
                    </main>
                    <footer>
                        <navbutton
                            :is-navigation="false"
                            :text="showConfirm ? confirmedLanguageVariables.confirm : $store.getters.textResource(`rs_language_prompt_save_button`)"
                            @element-click="saveButtonClicked"
                            styling="secondary"
                            :id="`save`"
                        />
                        <navbutton
                            :is-navigation="false"
                            @element-click="cancelButtonClicked"
                            :text="showConfirm ? confirmedLanguageVariables.cancel : $store.getters.textResource(`rs_language_prompt_cancel_button`)"
                            styling="tertiary"
                            :id="`cancel`"
                        />
                    </footer>
                </div>
            </div>
        </transition>
    </div>
</template>
<script>
import Navbutton from "./Navbutton.vue";
import methods from "@/global/methods.js";

export default {
    components: { Navbutton },
    name: "language-switcher",
    data() {
        return {
            selectboxIsExpanded: false,
            selectedLanguage: ``, // The language that has been selected in the popup
            surveyLanguage: ``, // The language the survey currently is in. The survey won't change language until the popup is gone
            confirmedLanguageVariables: {
                header: "",
                subtitle: "",
                confirm: "",
                cancel: "",
            },
            showDialog: false,
            showConfirm: false,
            fromLogin: false,
            changeLanguageArgs: {
                language: ``,
                fromLogin: ``
            }
        };
    },
    mounted() {
        this.surveyLanguage = this.selectedLanguage = this.currentLanguage.translatedName;
        this.fromLogin = this.$route.name === `Beginpage` || this.$route.name === `Loginpage`;
    },
    computed: {
        selectItems() {
            return this.$refs[`select-items`];
        },
        state() {
            return this.$store.state;
        },
        surveyInfo() {
            return this.state.surveyInfo;
        },
        languages() {
            return this.state.languages;
        },
        // The currently selected language
        currentLanguage() {
            if (this.selectedLanguage !== ``) {
                return this.languages.find((l) => l.name === this.selectedLanguage || l.translatedName === this.selectedLanguage);
            } else {
                return this.languages.find((x) => x.culture === this.surveyInfo.culture);
            }
        },
    },
    methods: {
        saveButtonClicked(e) {
            e.stopPropagation();
            if (this.showConfirm) {
                this.showDialog = false;
                this.showConfirm = false;
                this.selectboxIsExpanded = false;
                this.surveyLanguage = this.currentLanguage.culture;
                this.changeLanguageArgs.language = this.currentLanguage.language;
                this.changeLanguageArgs.fromLogin = this.fromLogin;
                this.$store.dispatch(`changeLanguage`, this.changeLanguageArgs);
            } else {
                this.showConfirm = true;

                methods.xhr({
                    type: "GET",
                    url: "/v1/LanguageSwitcher/GetMultiLanguageMenuVariables?cultureString=" + this.currentLanguage.culture + "&fromLogin=" + this.fromLogin,
                    callback: (xhr) => {
                        let response = JSON.parse(xhr.responseText);
               
                        this.confirmedLanguageVariables.header = response.header;
                        this.confirmedLanguageVariables.subtitle = response.subtitle; 
                        this.confirmedLanguageVariables.confirm = response.confirm;
                        this.confirmedLanguageVariables.cancel = response.cancel;
    
                        this.$store.commit(`changeGlobal`, {
                            variableName: `showLoader`,
                            newValue: false,
                        });
                    },
                });
            }
        },
        cancelButtonClicked() {
            if (this.showConfirm) {
                this.showConfirm = false;
            } else {
                this.showDialog = false;
                this.selectboxIsExpanded = false;
            }
        },
        dropdownClicked() {
            this.selectboxIsExpanded = !this.selectboxIsExpanded;
        },
        optionSelected(language, e) {
            e.stopPropagation();

            this.selectedLanguage = language.translatedName;
            this.selectboxIsExpanded = false;
        },
        focusOut() {
            this.selectboxIsExpanded = false;
        },
        switcherClicked() {
            this.showDialog = true;
        },
        dialogBackgroundClicked() {
            this.showDialog = false;
            this.showConfirm = false;
            this.selectboxIsExpanded = false;
        },
        moveFocus(index) {
            this.selectItems[index]?.focus();
        },
        move(targetItem, event) {
            const index = this.selectItems.findIndex((e) => e === event.target);
            this.moveFocus(index + targetItem);
        },
    },
};
</script>
<style scoped>
[data-dialog-background] {
    background: rgba(69, 72, 72, 0.5);
    position: fixed;
    top: 0;
    opacity: 1;
    bottom: 0;
    left: 0;
    right: 0;
    justify-content: center;
    display: flex;
    cursor: pointer;
}

[data-language-preview] {
    pointer-events: none;
    text-transform: uppercase;
    font-size: 14px;
    color: #fff;
}

[data-dropdown] {
    position: relative;
    display: block;
    font-size: 13px;
    width: 100%;
    max-width: 600px;
    height: 100%;
    pointer-events: all;
    border: 2px solid #454848;
    border-radius: 5px;
    height: 60px;
    box-shadow: 0px -8px 34px 0px rgba(0, 0, 0, 0.05);
    display: flex;
    cursor: pointer;
    justify-content: space-between;
    align-items: center;
    padding: 0 30px;

    svg {
        pointer-events: none;
    }
}

ul {
    background-color: #fff;
    width: 100%;
    max-height: 250px;
    border: 2px solid #454848;
    border-radius: 0 0 5px 5px;
    border-top: none;
    box-shadow: 0px -8px 34px 0px rgba(0, 0, 0, 0.05);
    overflow: auto;
    padding: 0;
    z-index: 1;
    padding-top: 2px;
    margin: 0 0 0 -2px;
    width: calc(100% + 4px);
    min-width: 300px;
    position: absolute;
    display: none;
    left: 0;
    top: 53px;

    &[aria-expanded] {
        display: block;
    }

    &::-webkit-scrollbar {
        -webkit-appearance: none;
    }

    &::-webkit-scrollbar:vertical {
        width: 12px;
    }

    &::-webkit-scrollbar:horizontal {
        height: 12px;
    }

    &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.5);
        border-radius: 10px;
        border: 2px solid #ffffff;
    }

    &::-webkit-scrollbar-track {
        border-radius: 10px;
        background-color: #ffffff;
    }

    li {
        color: #454848;
        line-height: 1.5em;
        padding: 20px;
        text-decoration: none;
        width: 100%;
        display: block;
        cursor: pointer;

        &:nth-child(odd) {
            background: #f7f7f7;
        }

        &:hover {
            background-color: var(--primary-color);
            color: white;
        }
    }
}

header,
p {
    color: var(--QuestionStyling-color);
    font-family: var(--QuestionStyling-font-family-public-name);
}

p {
    margin-bottom: 0;
    font-size: 13px;
}

[data-language-switcher] {
    display: flex;
    position: absolute;
    justify-content: center;
    flex-direction: row;
    align-content: center;
    white-space: nowrap;
    top: 15px;
    right: 20px;
    padding: 10px;
    width: 60px;
    height: 60px;
    border-radius: 5px;
    background: var(--primary-color);
    justify-self: flex-end;
    align-items: center;
    cursor: pointer;
    z-index: 2;

    > * {
        font-size: calc(var(--QuestionStyling-font-size) * 1px);
        font-family: var(--QuestionStyling-font-family-public-name);
    }

    [data-language-preview] {
        font-size: 14px;
    }

    .fa-globe {
        color: #fff;
        height: 15px;
        width: 20px;
        &:hover {
            color: #fff;
        }
    }
    svg {
        color: var(--primary-color);
        height: 15px;
        width: 15px;

        &:hover {
            color: var(--primary-color);
        }
    }
}

[data-dialog] {
    display: flex;
    flex-direction: column;
    min-width: 300px;
    max-width: 400px;
    width: 100%;
    background: #fff;
    margin-top: 200px;
    align-items: flex-start;
    align-self: flex-start;
    border-radius: 5px;
    box-shadow: 0 0 5px #666;
    cursor: auto;
    white-space: initial;

    header {
        width: 100%;
        font-size: 16px;
        padding: 20px;
        display: flex;
        flex-direction: column;
        font-family: var(--QuestionStyling-font-family-public-name);
    }

    main {
        background: #f8f8f8;
        width: 100%;
        padding: 20px;
    }

    footer {
        width: 100%;
        padding: 20px;

        button {
            width: 100%;

            &:last-of-type {
                margin-top: 10px;
            }
        }
    }
}

.fade-enter-active {
    transition: all 0.4s;
}

.fade-leave-active {
    pointer-events: none;
    transition: all 0.4s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
